import React, { useRef, useEffect, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { Telegram, Mic, Stop, AddPhotoAlternate, Close } from '@mui/icons-material';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';

interface MessageInputProps {
  newMessage: string;
  setNewMessage: (message: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  /** Send a piece of text directly (used to auto-send a voice transcript). */
  onAutoSend?: (text: string) => void;
  /** Object-URL preview of the currently attached image, or null. */
  attachedPreview?: string | null;
  /** Called with a picked image file (camera or gallery). */
  onPickImage?: (file: File) => void;
  /** Remove the currently attached image. */
  onClearImage?: () => void;
  /** How the attached image should be treated. */
  imageMode?: 'ask' | 'check_work';
  /** Change the attached-image mode. */
  onChangeImageMode?: (mode: 'ask' | 'check_work') => void;
  isSendingMessage: boolean;
  isBrainProcessing: boolean;
  isConfigViewActive: boolean;
  isLoadingMessages: boolean;
  isDisabled?: boolean;
  shouldShowSidebar?: boolean; // For conditional sidebar offset
  quizOpen?: boolean; // When the side quiz panel is open, stop the input before it on large screens
  drawOpen?: boolean; // When the whiteboard is docked on the right, offset the input by its width
}

const MessageInput: React.FC<MessageInputProps> = ({
  newMessage,
  setNewMessage,
  onSubmit,
  onAutoSend,
  attachedPreview = null,
  onPickImage,
  onClearImage,
  imageMode = 'ask',
  onChangeImageMode,
  isSendingMessage,
  isBrainProcessing,
  isConfigViewActive,
  isLoadingMessages,
  isDisabled = false,
  shouldShowSidebar = true,
  quizOpen = false,
  drawOpen = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isWaitingForResponse = isSendingMessage || isBrainProcessing;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Keep a live ref of the current text so the transcription callback (created
  // once) can append to whatever is currently typed without going stale.
  const newMessageRef = useRef(newMessage);
  useEffect(() => {
    newMessageRef.current = newMessage;
  }, [newMessage]);

  // Keep a stable ref to the auto-send handler for the (once-created) callback.
  const onAutoSendRef = useRef(onAutoSend);
  useEffect(() => {
    onAutoSendRef.current = onAutoSend;
  }, [onAutoSend]);

  const handleTranscript = useCallback(
    (transcript: string) => {
      const current = newMessageRef.current;
      const needsSpace = current.length > 0 && !/\s$/.test(current);
      const next = `${current}${needsSpace ? ' ' : ''}${transcript}`;

      // Voice flow: once transcription is done, send automatically and clear
      // the box. Falls back to just filling the box if no auto-send handler.
      if (onAutoSendRef.current) {
        newMessageRef.current = '';
        setNewMessage('');
        onAutoSendRef.current(next);
      } else {
        newMessageRef.current = next;
        setNewMessage(next);
      }
    },
    [setNewMessage]
  );

  const {
    isSupported: isVoiceSupported,
    isRecording,
    isTranscribing,
    error: voiceError,
    toggleRecording,
    stopRecording,
  } = useVoiceRecorder({ onResult: handleTranscript });

  // Stop recording if the input gets disabled or a response starts processing.
  useEffect(() => {
    if ((isDisabled || isWaitingForResponse) && isRecording) {
      stopRecording();
    }
  }, [isDisabled, isWaitingForResponse, isRecording, stopRecording]);

  // Auto-focus the textarea when ready for input
  useEffect(() => {
    if (
      !isConfigViewActive && 
      !isWaitingForResponse && 
      !isLoadingMessages && 
      textareaRef.current
    ) {
      // Small delay to ensure everything is rendered
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isConfigViewActive, isWaitingForResponse, isLoadingMessages]);

  if (isConfigViewActive) {
    return null;
  }

  // Conditionally apply sidebar offset only when sidebar is shown
  const sidebarOffsetClass = shouldShowSidebar ? 'lg:left-72' : 'left-0';

  // When the quiz panel is open, stop the input bar before the panel on large
  // screens so it stays under the conversation area only. On smaller screens the
  // panel overlays, so the input keeps the full width.
  const quizOffsetClass = quizOpen ? 'lg:right-[420px] xl:right-[460px]' : '';

  const handleSubmit = (e: React.FormEvent) => {
    if (isRecording) {
      stopRecording();
    }
    onSubmit(e);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onPickImage) onPickImage(file);
    // Reset so picking the same file again re-triggers onChange.
    e.target.value = '';
  };

  const micDisabled = isWaitingForResponse || isDisabled || isTranscribing;
  const attachDisabled = isWaitingForResponse || isDisabled;
  // Sendable with text OR an attached image (image-only turns are valid).
  const canSend = (!!newMessage.trim() || !!attachedPreview) && !isWaitingForResponse && !isDisabled;

  return (
    <div className={`fixed bottom-0 z-50 ${drawOpen ? 'right-[440px]' : 'right-0'} ${sidebarOffsetClass} ${quizOffsetClass}`}>
      <div className="floating-input-bg">
        <div className="w-full max-w-4xl mx-auto">
          {attachedPreview && (
            <div className="mb-2 flex items-center gap-3">
              <div className="relative inline-block">
                <img
                  src={attachedPreview}
                  alt="Attached"
                  className="h-16 w-16 rounded-lg border border-slate-200 object-cover"
                />
                <button
                  type="button"
                  onClick={onClearImage}
                  aria-label="Remove attached image"
                  title="Remove image"
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white shadow hover:bg-slate-900"
                >
                  <Close sx={{ fontSize: 14 }} />
                </button>
              </div>
              {/* Mode toggle: explain the image vs review the student's own work. */}
              <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-[11px] sm:text-xs">
                <button
                  type="button"
                  onClick={() => onChangeImageMode?.('ask')}
                  className={`rounded-full px-3 py-1 transition-colors ${
                    imageMode === 'ask' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Ask about this
                </button>
                <button
                  type="button"
                  onClick={() => onChangeImageMode?.('check_work')}
                  className={`rounded-full px-3 py-1 transition-colors ${
                    imageMode === 'check_work' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Check my work
                </button>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
            />
            <textarea
              ref={textareaRef}
              className="floating-input"
              rows={1}
              placeholder={
                isWaitingForResponse
                  ? 'Curiosity Coach is replying...'
                  : isRecording
                  ? 'Listening… I’ll send when you pause'
                  : isTranscribing
                  ? 'Transcribing your voice…'
                  : 'Ask about homework, class, or anything you want to understand better'
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!isWaitingForResponse && !isDisabled) {
                    handleSubmit(e);
                  }
                }
              }}
              disabled={isDisabled}
            />
            {onPickImage && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachDisabled}
                aria-label="Attach a photo"
                title="Attach a photo of your work or a problem"
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <AddPhotoAlternate fontSize="medium" />
              </button>
            )}
            {isVoiceSupported && (
              <button
                type="button"
                onClick={toggleRecording}
                disabled={micDisabled}
                aria-pressed={isRecording}
                aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
                title={isRecording ? 'Stop now' : 'Speak your message — sends automatically when you pause'}
                className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {isTranscribing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isRecording ? (
                  <Stop fontSize="medium" />
                ) : (
                  <Mic fontSize="medium" />
                )}
              </button>
            )}
            <button
              type="submit"
              className={`btn-gradient-primary ${isWaitingForResponse ? 'animate-pulse' : ''}`}
              disabled={!canSend}
            >
              {isWaitingForResponse ? (
                  <CircularProgress size={24} color="inherit" />
              ) : (
                  <Telegram fontSize="medium" />
              )}
            </button>
          </form>
          <div className="mt-2 flex items-center justify-between px-1 text-[11px] text-slate-500 sm:text-xs">
            {voiceError ? (
              <span className="text-red-500">{voiceError}</span>
            ) : isRecording ? (
              <span className="text-red-500">Listening… speak in any language; it sends when you pause.</span>
            ) : isTranscribing ? (
              <span className="text-indigo-500">Transcribing your voice…</span>
            ) : (
              <span>Press Enter to send. Shift+Enter adds a new line.</span>
            )}
            <span className="hidden sm:inline">Stay curious. Keep digging ✦</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageInput;
